/**
 * Les papiers d'équipe, adossés à Supabase.
 *
 * L'état — expiré, à réserver, réservé — se calcule dans `core` ; cet
 * adaptateur lit et écrit. Les droits sont en base : le bureau lit tout, le
 * compagnon ses propres lignes.
 */
import { DomainError } from "@dker/core";
import type {
  Certification,
  CertificationRepository,
  ExecutionContext,
  SaveCertificationInput,
} from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

const COLONNES =
  "id, employee_id, kind, label, obtained_on, expires_on, renewal_planned_on, notes";

function toCertification(row: Row): Certification {
  return {
    id: row.id,
    employeeId: row.employee_id,
    kind: row.kind,
    label: row.label ?? null,
    obtainedOn: row.obtained_on ?? null,
    expiresOn: row.expires_on ?? null,
    renewalPlannedOn: row.renewal_planned_on ?? null,
    notes: row.notes ?? null,
  };
}

export function createSupabaseCertificationRepository(
  clientFor: DbClientFactory,
): CertificationRepository {
  return {
    async list(ctx: ExecutionContext, employeeId?: string) {
      let q = clientFor(ctx)
        .from("employee_certifications")
        .select(COLONNES)
        .order("expires_on", { ascending: true, nullsFirst: false });
      if (employeeId) q = q.eq("employee_id", employeeId);
      const { data, error } = await q;
      if (error) fail(error);
      return ((data ?? []) as Row[]).map(toCertification);
    },

    async listMine(ctx: ExecutionContext) {
      const db = clientFor(ctx);
      const { data: moi, error: e1 } = await db.rpc("my_employee_id");
      if (e1) fail(e1);
      if (!moi) return [];
      const { data, error } = await db
        .from("employee_certifications")
        .select(COLONNES)
        .eq("employee_id", moi as string)
        .order("expires_on", { ascending: true, nullsFirst: false });
      if (error) fail(error);
      return ((data ?? []) as Row[]).map(toCertification);
    },

    async save(ctx: ExecutionContext, command: Omit<SaveCertificationInput, "confirmationId">) {
      const ligne = {
        employee_id: command.employeeId,
        kind: command.kind,
        label: command.label,
        obtained_on: command.obtainedOn,
        expires_on: command.expiresOn,
        renewal_planned_on: command.renewalPlannedOn,
        notes: command.notes,
      };
      const q = clientFor(ctx).from("employee_certifications");
      const { data, error } = command.id
        ? await q.update(ligne).eq("id", command.id).select(COLONNES).single()
        : await q.insert(ligne).select(COLONNES).single();
      if (error) fail(error);
      return toCertification(data as Row);
    },

    async remove(ctx: ExecutionContext, id: string) {
      const { error } = await clientFor(ctx).from("employee_certifications").delete().eq("id", id);
      if (error) fail(error);
    },
  };
}
